import { FormInputTypes, GridColSpan, TypedFormInputs } from 'components/form';
import {
  emailAddressValidation,
  phoneNumberValidation,
  webURLValidation,
  zipCodeValidation,
} from 'utils/validation';
import { statesOptions } from 'utils/constants';
import { EnvelopeIcon, GlobeIcon, MapMarkerAltIcon } from 'components/icons';
import { fillAddressDetails } from 'components/placesAutocomplete/utils';
import { ContractorInfo } from 'containers/contractor/types';
import { stringifyEmptyFields } from 'utils';
import { AddContractorModalProps, EditContractorFormData } from './types';

export const getFormInputs = (
  props?: AddContractorModalProps,
): TypedFormInputs<EditContractorFormData> => ({
  // organization
  name: {
    element: FormInputTypes.Field,
    label: 'Display name',
    elementProps: {
      disabled: !!props?.existingOrganization,
    },
    validation: {
      required: true,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  legalName: {
    element: FormInputTypes.Field,
    elementProps: {
      disabled: !!props?.existingOrganization,
    },
    label: 'Legal Name',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  addressLine1: {
    element: FormInputTypes.PlacesAutocomplete,
    label: 'Address',
    elementProps: {
      disabled: !!props?.existingOrganization,
      placeholder: 'Address',
      icon: MapMarkerAltIcon,
      onCommit: fillAddressDetails,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  addressLine2: {
    element: FormInputTypes.Field,
    elementProps: {
      disabled: !!props?.existingOrganization,
    },
    label: 'Address, Line 2',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  addressCity: {
    element: FormInputTypes.Field,
    elementProps: {
      disabled: !!props?.existingOrganization,
    },
    label: 'City',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  addressState: {
    element: FormInputTypes.Select,
    label: 'State',
    elementProps: {
      disabled: !!props?.existingOrganization,
      options: statesOptions,
    },
    layout: [GridColSpan.Span2, GridColSpan.SmSpan1],
  },
  addressZipCode: {
    element: FormInputTypes.Field,
    label: 'ZIP Code',
    layout: [GridColSpan.Span2, GridColSpan.SmSpan1],
    elementProps: {
      disabled: !!props?.existingOrganization,
      fieldType: 'zipcode',
    },
    validation: {
      pattern: zipCodeValidation,
    },
  },
  // billing contact
  contactFirstName: {
    element: FormInputTypes.Field,
    label: 'Contact First Name',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  contactLastName: {
    element: FormInputTypes.Field,
    label: 'Contact Last Name',
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  contactEmail: {
    element: FormInputTypes.Field,
    label: 'Contact Email',
    elementProps: {
      icon: EnvelopeIcon,
    },
    validation: { validate: emailAddressValidation() },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  contactPhoneNumber: {
    element: FormInputTypes.Field,
    label: 'Contact Phone',
    elementProps: {
      fieldType: 'phone',
      showDefaultIcon: true,
    },
    validation: {
      pattern: phoneNumberValidation,
    },
    layout: [GridColSpan.SpanFull, GridColSpan.SmSpan2],
  },
  websiteUrl: {
    element: FormInputTypes.Field,
    label: 'Website URL',
    elementProps: {
      icon: GlobeIcon,
    },
    validation: {
      pattern: webURLValidation,
    },
    layout: [GridColSpan.SpanFull],
  },
  description: {
    element: FormInputTypes.TextAreaField,
    label: 'Description',
    layout: [GridColSpan.SpanFull],
  },
});

export const getDefaultValues = (
  contractor: ContractorInfo,
): EditContractorFormData => {
  const { organization, billingContact, websiteUrl, description } =
    contractor ?? {};
  const {
    name,
    legalName,
    addressLine1,
    addressLine2,
    addressCity,
    addressState,
    addressZipCode,
  } = organization ?? {};
  const {
    firstName: contactFirstName,
    lastName: contactLastName,
    email: contactEmail,
    phoneNumber: contactPhoneNumber,
  } = billingContact ?? {};

  return {
    ...stringifyEmptyFields({
      // organization
      name,
      legalName,
      addressLine1,
      addressLine2,
      addressCity,
      addressZipCode,

      // billing contact
      contactFirstName,
      contactLastName,
      contactEmail,
      contactPhoneNumber,

      websiteUrl,
      description,
    }),
    addressState:
      statesOptions.find((option) => option.value === addressState) ?? null,
  };
};
